import React from "react";
import { CreateTrackRequest, TrackType } from "@/types";
import { useTranslation } from "react-i18next";

interface TrackFormProps {
  trackData: Partial<CreateTrackRequest>;
  errors: Record<string, string>;
  onUpdateField: (
    field: keyof CreateTrackRequest,
    value: string | number | undefined,
  ) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  isEditing: boolean;
}

const trackTypeOptions: TrackType[] = [
  "circuit",
  "gymkhana",
  "drag_strip",
  "hill_climb",
  "rally",
  "oval",
  "karting",
  "other",
];

export const TrackForm: React.FC<TrackFormProps> = ({
  trackData,
  errors,
  onUpdateField,
  onSubmit,
  onCancel,
  isSubmitting,
  isEditing,
}) => {
  const { t } = useTranslation(["pages", "common"]);
  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            {t("pages:tracks.form.name")} *
          </label>
          <input
            type="text"
            value={trackData.name || ""}
            onChange={(e) => onUpdateField("name", e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.name ? "border-red-500" : "border-secondary-300"
            }`}
            placeholder={t("pages:tracks.form.namePlaceholder")}
            required
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            {t("pages:tracks.form.type")} *
          </label>
          <select
            value={trackData.type || ""}
            onChange={(e) => onUpdateField("type", e.target.value as TrackType)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.type ? "border-red-500" : "border-secondary-300"
            }`}
            required
          >
            <option value="">{t("pages:tracks.form.selectType")}</option>
            {trackTypeOptions.map((option) => (
              <option key={option} value={option}>
                {t(`pages:tracks.form.typeOptions.${option}`)}
              </option>
            ))}
          </select>
          {errors.type && (
            <p className="mt-1 text-sm text-red-600">{errors.type}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            {t("pages:tracks.form.latitude")} *
          </label>
          <input
            type="number"
            step="any"
            value={trackData.latitude || ""}
            onChange={(e) =>
              onUpdateField("latitude", parseFloat(e.target.value))
            }
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.latitude ? "border-red-500" : "border-secondary-300"
            }`}
            placeholder={t("pages:tracks.form.latitudePlaceholder")}
            required
          />
          {errors.latitude && (
            <p className="mt-1 text-sm text-red-600">{errors.latitude}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            {t("pages:tracks.form.longitude")} *
          </label>
          <input
            type="number"
            step="any"
            value={trackData.longitude || ""}
            onChange={(e) =>
              onUpdateField("longitude", parseFloat(e.target.value))
            }
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.longitude ? "border-red-500" : "border-secondary-300"
            }`}
            placeholder={t("pages:tracks.form.longitudePlaceholder")}
            required
          />
          {errors.longitude && (
            <p className="mt-1 text-sm text-red-600">{errors.longitude}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            {t("pages:tracks.form.length")}
          </label>
          <input
            type="number"
            step="0.01"
            value={trackData.length || ""}
            onChange={(e) =>
              onUpdateField(
                "length",
                e.target.value ? parseFloat(e.target.value) : undefined,
              )
            }
            className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder={t("pages:tracks.form.lengthPlaceholder")}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            {t("pages:tracks.form.numberOfTurns")}
          </label>
          <input
            type="number"
            value={trackData.numberOfTurns || ""}
            onChange={(e) =>
              onUpdateField(
                "numberOfTurns",
                e.target.value ? parseInt(e.target.value) : undefined,
              )
            }
            className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder={t("pages:tracks.form.numberOfTurnsPlaceholder")}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            {t("pages:tracks.form.elevation")}
          </label>
          <input
            type="number"
            step="0.1"
            value={trackData.elevation || ""}
            onChange={(e) =>
              onUpdateField(
                "elevation",
                e.target.value ? parseFloat(e.target.value) : undefined,
              )
            }
            className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder={t("pages:tracks.form.elevationPlaceholder")}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            {t("pages:tracks.form.lapRecord")}
          </label>
          <input
            type="number"
            value={trackData.lapRecord || ""}
            onChange={(e) =>
              onUpdateField(
                "lapRecord",
                e.target.value ? parseInt(e.target.value) : undefined,
              )
            }
            className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder={t("pages:tracks.form.lapRecordPlaceholder")}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            {t("pages:tracks.form.lapRecordHolder")}
          </label>
          <input
            type="text"
            value={trackData.lapRecordHolder || ""}
            onChange={(e) =>
              onUpdateField("lapRecordHolder", e.target.value || undefined)
            }
            className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder={t("pages:tracks.form.lapRecordHolderPlaceholder")}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          {t("pages:tracks.form.yearOpened")}
        </label>
        <input
          type="number"
          value={trackData.yearOpened || ""}
          onChange={(e) =>
            onUpdateField(
              "yearOpened",
              e.target.value ? parseInt(e.target.value) : undefined,
            )
          }
          className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder={t("pages:tracks.form.yearOpenedPlaceholder")}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          {t("common:description")}
        </label>
        <textarea
          value={trackData.description || ""}
          onChange={(e) => onUpdateField("description", e.target.value)}
          rows={4}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.description ? "border-red-500" : "border-secondary-300"
          }`}
          placeholder={t("pages:tracks.form.descriptionPlaceholder")}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description}</p>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-secondary-300 rounded-lg text-secondary-700 hover:bg-secondary-50 transition-colors"
          disabled={isSubmitting}
        >
          {t("common:cancel")}
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting
            ? t("common:saving")
            : isEditing
              ? t("pages:tracks.form.update")
              : t("pages:tracks.form.create")}
        </button>
      </div>
    </form>
  );
};
